export type LocalTranslator = {
  translate(text: string, options?: { signal?: AbortSignal }): Promise<string>;
  destroy(): void;
};

type TranslatorFactory = {
  availability?(options: { sourceLanguage: string; targetLanguage: string }): Promise<"available" | "downloadable" | "downloading" | "unavailable">;
  create(options: { sourceLanguage: string; targetLanguage: string; signal?: AbortSignal }): Promise<LocalTranslator>;
};

export function localTranslator(): TranslatorFactory | undefined {
  return (globalThis as typeof globalThis & { Translator?: TranslatorFactory }).Translator;
}

type LocalDetector = { detect(text: string): Promise<{ detectedLanguage: string; confidence: number }[]>; destroy(): void };
type DetectorFactory = { availability(): Promise<string>; create(): Promise<LocalDetector> };
export async function detectMailLanguage(text: string): Promise<string | null> {
  if (text.trim().length < 80) return null;
  const factory = (globalThis as typeof globalThis & { LanguageDetector?: DetectorFactory }).LanguageDetector;
  if (!factory) return null;
  let detector: LocalDetector | undefined;
  try {
    if (await factory.availability() !== "available") return null;
    detector = await factory.create();
    const results = await detector.detect(text.slice(0, 4000));
    const first = results[0];
    const language = first?.detectedLanguage.split("-")[0];
    if (first && first.confidence >= 0.7 && first.confidence - (results[1]?.confidence || 0) >= 0.2 && ["en", "ja", "ko", "fr", "de", "es", "zh"].includes(language || "")) return language!;
  } catch { /* Permission policy or a missing local pack leaves the original language choice available. */ }
  finally { detector?.destroy(); }
  return null;
}

async function translateText(text: string, translator: LocalTranslator, signal: AbortSignal): Promise<string> {
  const trimmed = text.trim();
  if (!trimmed) return text;
  const start = text.indexOf(trimmed);
  const characters = Array.from(trimmed);
  const chunks: string[] = [];
  for (let offset = 0; offset < characters.length; offset += 1500) {
    if (signal.aborted) throw new DOMException("Translation canceled", "AbortError");
    chunks.push(await translator.translate(characters.slice(offset, offset + 1500).join(""), { signal }));
  }
  return text.slice(0, start) + chunks.join("") + text.slice(start + trimmed.length);
}

export async function translateMailText(text: string, translator: LocalTranslator, signal: AbortSignal) {
  return translateText(text, translator, signal);
}

export async function translateMailHtml(html: string, translator: LocalTranslator, signal: AbortSignal, onProgress: (done: number, total: number) => void) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const root = doc.querySelector(".mail-document");
  if (!root) throw new Error("邮件正文无法解析，请切换纯文本查看。");
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (node.textContent?.trim() && !node.parentElement?.closest("style,script,svg,math")) nodes.push(node);
  }
  for (let index = 0; index < nodes.length; index++) {
    if (signal.aborted) throw new DOMException("Translation canceled", "AbortError");
    nodes[index].textContent = await translateText(nodes[index].textContent || "", translator, signal);
    if (index % 10 === 9 || index === nodes.length - 1) onProgress(index + 1, nodes.length);
  }
  doc.documentElement.lang = "zh-CN";
  return "<!doctype html>" + doc.documentElement.outerHTML;
}
